import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import type {
  ReorderOrganizationItemsRequest,
  ReorderOrganizationItemsResponse,
} from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';

const endpoint = 'admin/organization/display-order';

// Authentication, admin authorization, and organization ownership are enforced
// by the server; the client sends only the item type and requested relative order.
const fetcher = async (
  _key: string,
  { arg }: { arg: ReorderOrganizationItemsRequest }
): Promise<ReorderOrganizationItemsResponse> =>
  denApi.patch<
    ReorderOrganizationItemsResponse,
    ReorderOrganizationItemsRequest
  >(endpoint, arg);

/**
 * Exposes the display-order PATCH request as an SWR mutation.
 *
 * This transport hook deliberately owns no optimistic state or rollback policy;
 * `useOrganizationItemOrder` coordinates those against the caller's source list.
 * Optional SWR callbacks remain available for consumers that need request-level
 * instrumentation without changing that ordering lifecycle.
 */
export function useReorderOrganizationItems(
  swrOptions?: SWRMutationConfiguration<
    ReorderOrganizationItemsResponse,
    Error,
    string,
    ReorderOrganizationItemsRequest
  >
) {
  const { trigger, isMutating } = useSWRMutation<
    ReorderOrganizationItemsResponse,
    Error,
    string,
    ReorderOrganizationItemsRequest
  >(endpoint, fetcher, swrOptions);

  return {
    reorderOrganizationItems: trigger,
    isReordering: isMutating,
  };
}
